import { ofType } from 'redux-observable';
import { filter, mergeMap, map, of } from 'rxjs';
import { isEmpty, isNil } from 'lodash';
import { AppEpic, AppState } from 'src/store';
import { receiveViewDefns, updateGroupBy, updateTopMemberSelection } from 'src/components/Subheader/Subheader.slice';
import {
  cleanCollectionViewUp,
  fetchCollectionViewData,
  receiveCollectionViewTenantConfig,
} from './CollectionView.slice';
import {
  inputIsNotNullOrUndefined,
  isSameComponentType,
  isScopeDefined,
  isSubheaderLoaded,
  maybeGetComponentProps,
  topMemInWorklistSelected,
} from 'src/utils/Functions/epicsFunctions';
import { ConfDefnComponentType, CollectionViewComponent } from 'configuration-schemas/confdefnComponents';
import { isListDataApi } from 'configuration-schemas/confdefnView';
import { ExtraPivotOptions, getAggBys, organizeListDataOptions } from '../StyleColorReview.slice';
import {
  receiveScope,
  receiveScopeRefreshTrigger,
} from 'src/components/AssortmentScopeSelector/AssortmentScopeSelector.slice';
import { receiveFilterStateAfterSubmission } from 'src/components/FilterPanel/FilterPanel.slice';
import { updateSelectedItem } from 'src/pages/Worklist/Worklist.slice';
import { LENS_ASST_PLAN_REFRESHING_PAGE } from 'src/pages/AssortmentBuild/Planning.types';
import { fetchFloorsetData, receiveFloorsetData } from 'src/components/ConfigurableGrid/ConfigurableGrid.slice';
import { REQUEST_EXTERNAL_DATA_REFRESH } from 'src/components/higherOrder/GlobalDataRefresh';

const filterFloorsetApi = (floorsetApi: CollectionViewComponent['componentProps']['floorsetApi'], state: AppState) =>
  isNil(floorsetApi) || !isEmpty(state.subheader.topMemberOptions);

export const collectionViewLoad: AppEpic = (action$, state$) => {
  return action$.pipe(
    ofType(
      receiveScopeRefreshTrigger.type,
      REQUEST_EXTERNAL_DATA_REFRESH,
      receiveFilterStateAfterSubmission.type,
      receiveCollectionViewTenantConfig.type,
      // Instead of listening to activePage or activeSubPage, listen to the subheader finishing
      // fetching the groupBy/sortBy defns. This is to prevent collection view breaking when
      // switching from a screen with a groupBy active that is not valid in collection view.
      // If we run the epic too early, it applies the invalid groupBy
      receiveViewDefns.type,
      receiveScope.type,
      updateSelectedItem.type,
      updateGroupBy.type,
      updateTopMemberSelection.type,
      receiveFloorsetData.type,
      LENS_ASST_PLAN_REFRESHING_PAGE,
    ),
    map(() => maybeGetComponentProps<CollectionViewComponent>(state$.value, ConfDefnComponentType.collectionView)),
    filter(inputIsNotNullOrUndefined),
    filter(() => topMemInWorklistSelected(state$.value)),
    filter(() => isScopeDefined(state$.value.scope) && isSubheaderLoaded(state$.value.subheader)),
    filter(({ floorsetApi }) => filterFloorsetApi(floorsetApi, state$.value)),
    mergeMap(({ defns, topMembers: worklistTopMembers, floorsetApi }) => {
      const { dataApi, model: modelDefn } = defns;
      const finalModelDefn = isListDataApi(dataApi) ? dataApi.defnId : modelDefn;
      const aggBy = getAggBys(state$.value.subheader, dataApi);
      // dont use floorsets if api is not configured
      const { topMemberSelection: floorsetSelection } = state$.value.subheader;
      const maybeFloorsetTopMember = floorsetApi ? floorsetSelection : undefined;

      // combine top members incase worklist has one as well
      const topMembers = [worklistTopMembers, maybeFloorsetTopMember].filter((v) => !isNil(v) && v !== '').join(',');

      const options: ExtraPivotOptions = topMembers !== '' ? { topMembers, aggBy } : { aggBy };
      const finalOptions = organizeListDataOptions(options, dataApi);

      if (isSameComponentType<CollectionViewComponent>(state$.value, ConfDefnComponentType.collectionView)) {
        return of(cleanCollectionViewUp(), fetchCollectionViewData(finalModelDefn, finalOptions));
      }

      return of(fetchCollectionViewData(finalModelDefn, finalOptions));
    }),
  );
};

export const fetchCollectionViewFloorsetsOnLoad: AppEpic = (action$, state$) => {
  return action$.pipe(
    ofType(
      receiveScope.type,
      receiveScopeRefreshTrigger.type,
      receiveFilterStateAfterSubmission.type,
      receiveCollectionViewTenantConfig.type,
      REQUEST_EXTERNAL_DATA_REFRESH,
    ),
    map(() => maybeGetComponentProps<CollectionViewComponent>(state$.value, ConfDefnComponentType.collectionView)),
    filter(inputIsNotNullOrUndefined),
    filter(() => topMemInWorklistSelected(state$.value)),
    filter(() => isScopeDefined(state$.value.scope)),
    mergeMap(({ floorsetApi }) => of(fetchFloorsetData(floorsetApi))),
  );
};
